import type {
  ReactNode,
} from 'react';
import type {
  WishlistEntity,
} from '@dinosave/core/types';
import Button from '@/ui/components/Button';
import Text from '@/ui/components/Text';
import {
  LockIcon,
  UsersIcon,
  GlobeIcon,
  CheckmarkIcon,
} from '@/ui/icons';

import styles from './WishlistPrivacy.module.css';

type WishlistPrivacyProps = {
  value: WishlistEntity['privacy'];
  onChange?: (option: WishlistEntity['privacy']) => void;
}

const PRIVACY_OPTIONS: {
  icon: ReactNode;
  label: string;
  description: string;
  value: WishlistEntity['privacy'];
}[] = [
  {
    icon: <GlobeIcon />,
    value: 'public',
    label: 'Public',
    description: 'Visible to anyone',
  },
  {
    icon: <UsersIcon />,
    value: 'friends',
    label: 'Friends',
    description: 'Visible to friends',
  },
  {
    icon: <LockIcon />,
    value: 'private',
    label: 'Private',
    description: 'Visible only to you',
  },
];

type PrivacyOption = typeof PRIVACY_OPTIONS[number];

const WishlistPrivacy = (props: WishlistPrivacyProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    value,
    onChange,
  } = props;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handlePrivacyChange = (option: PrivacyOption) => () => {
    if (
      onChange
    ) onChange(option.value);
  };

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const privacyOptionClassName = (isActive: boolean) => (
    `${styles.privacyOption} ` +
    `${isActive ? styles.privacyOptionActive : ''} `
  );

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={styles.wishlistPrivacy}>
      {PRIVACY_OPTIONS.map((option) => {
        const isActive = value === option.value;

        return (
          <Button
            key={option.value}
            kind={isActive ? 'outline' : 'default'}
            color={isActive ? 'primary' : 'secondary'}
            onClick={handlePrivacyChange(option)}
            roundness="rounded"
          >
            <div className={privacyOptionClassName(isActive)}>
              <div className={styles.privacyOptionIcon}>
                {option.icon}
              </div>

              <div className={styles.privacyOptionLabel}>
                <Text
                  kind="caption"
                  size="large"
                  weight="500"
                >
                  {option.label}
                </Text>
              </div>

              <div className={styles.privacyOptionDescription}>
                <Text
                  kind="caption"
                  size="small"
                  color="neutral-500"
                  weight="500"
                >
                  {option.description}
                </Text>
              </div>

              {isActive && (
                <div className={styles.privacyOptionCheck}>
                  <CheckmarkIcon />
                </div>
              )}
            </div>
          </Button>
        );
      })}
    </div>
  );
};

export default WishlistPrivacy;
